import { Dropdown } from 'antd';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAnalytics from '@shared/useAnalytics';
import { PARAMETERS } from '@root/tracking/constants';
import useEnv from '@shared/useEnv';
import cs from 'classnames';
import { truncate } from 'lodash';
import contentWidth from '@styles/contentWidth.module.less';
import CustomIcon from '@components/shared/CustomIcon';
import { CurrentUser } from '@root/types';
import routes from '@root/routes';
import Logo from '@components/layouts/Truva/Layout/Navigation/Logo';
import UniversalSearch from '../UniversalSearch';
import { TrackerProps } from '../Navigation';
import s from './StandardNavigation.module.less';

type FavoritesProps = {
  trackers: TrackerProps;
};

const Favorites = ({ trackers }: FavoritesProps) => {
  const { t } = useTranslation('navigation');
  const { savedItemsClicked } = trackers;

  return (
    <li key="my-favorites" className={s.navigationLink}>
      <Dropdown
        trigger={['hover', 'click']}
        overlay={
          <ul className={s.menu}>
            <li className={s.menuItem}>
              <NavLink
                to={routes.savedListings}
                onClick={() => savedItemsClicked('saved listings page')}
              >
                {t('listings')}
              </NavLink>
            </li>
            <li className={s.menuItem}>
              <NavLink
                to={routes.savedSearches}
                onClick={() => savedItemsClicked('saved search page')}
              >
                {t('searches')}
              </NavLink>
            </li>
            <li className={s.menuItem}>
              <NavLink
                to={routes.savedBuildings}
                onClick={() => savedItemsClicked('saved buildings page')}
              >
                {t('buildings')}
              </NavLink>
            </li>
            <li className={s.menuItem}>
              <NavLink
                to={routes.savedBuildingOwners}
                onClick={() => savedItemsClicked('saved building owners page')}
              >
                {t('buildingOwners')}
              </NavLink>
            </li>
          </ul>
        }
      >
        <div data-testid="savedDropdownMeny" key="saved-items-link">
          <NavLink
            to={routes.savedListings}
            onClick={() => savedItemsClicked('saved listings page')}
          >
            <span className={s.icon}>
              <CustomIcon type="heart-outline" />
            </span>
            <span className={s.vtsNavText}>{t('favorites')}</span>
          </NavLink>
        </div>
      </Dropdown>
    </li>
  );
};

type UserSettingsProps = {
  currentUser: CurrentUser;
  trackers: TrackerProps;
};

const UserSettings = ({ currentUser, trackers }: UserSettingsProps) => {
  const { t } = useTranslation('navigation');

  return (
    <li key="user-dropdown" className={s.navigationLink}>
      <Dropdown
        trigger={['hover', 'click']}
        overlay={
          <ul className={s.menu}>
            <li className={s.menuItem}>
              <NavLink
                to={routes.users.edit('professional_info')}
                onClick={trackers.accountClicked}
              >
                {t('account')}
              </NavLink>
            </li>
            <li className={s.menuItem}>
              <a href="/logout">{t('signOut')}</a>
            </li>
          </ul>
        }
      >
        <div data-testid="userDropdownMenu" key="user-settings-link">
          <NavLink to={routes.users.edit('professional_info')} onClick={trackers.nameClicked}>
            <span className={s.icon}>
              <CustomIcon type="person-circle" />
            </span>
            <span className={s.vtsNavText}>
              {currentUser.firstName
                ? truncate(currentUser.firstName, {
                    length: 16,
                  })
                : currentUser.email}
            </span>
          </NavLink>
        </div>
      </Dropdown>
    </li>
  );
};

const VTSHomeButton = () => {
  const { t } = useTranslation('navigation');

  return (
    <li key="vts-home-button" className={s.vtsHomeButtonContainer}>
      <a href="https://app.vts.com">
        <div>
          <span className={s.vtsHomeButton}>{t('vtsHome')}</span>
        </div>
      </a>
    </li>
  );
};

const OverflowOptionsForTablet = () => {
  const { t } = useTranslation('navigation');

  return (
    <li key="vts-home-dropdwon" className={s.vtsHomeDropdownMenu}>
      <Dropdown
        trigger={['hover', 'click']}
        overlay={
          <ul className={s.menu}>
            <li className={s.menuItem}>
              <a href="https://app.vts.com">{t('vtsHome')}</a>
            </li>
          </ul>
        }
      >
        <div key="user-settings-link">
          <span className={s.icon}>
            <CustomIcon type="overflow" />
          </span>
        </div>
      </Dropdown>
    </li>
  );
};

const SignedOutNavigation = () => {
  const { t } = useTranslation('navigation');
  const { vtsUrl } = useEnv();
  const { clickToPage, anonymousId } = useAnalytics();
  return (
    <>
      <li className={s.navigationLink} key="sign-in-link">
        <a
          href={routes.signUp(vtsUrl!, {
            utm_content: 'marketplace nav',
            anonymous_id: anonymousId,
          })}
          onClick={() => {
            clickToPage({
              destination: PARAMETERS.signUpPage,
              sourceContent: PARAMETERS.navigation,
            });
          }}
        >
          {t('signUp')}
        </a>
      </li>
      <li className={s.navigationLink} key="sign-up-link">
        <a href="/login">{t('logIn')}</a>
      </li>
    </>
  );
};

type Props = {
  currentUser?: CurrentUser | null;
  showUniversalSearch?: boolean;
  isCurrentUserLoaded?: boolean;
  trackers: TrackerProps;
  hideLogInAndSignUp: boolean;
};

const StandardNavigation = ({
  currentUser = null,
  showUniversalSearch = false,
  isCurrentUserLoaded = false,
  trackers,
  hideLogInAndSignUp = false,
}: Props) => {
  const location = useLocation();
  const { t } = useTranslation('navigation');
  const flags = useFlags();

  let navClassName = cs(s.navigation, contentWidth.contentContainer, contentWidth.contentPadding);
  if (flags['full-width-navigation']) {
    navClassName = cs(s.navigation, s.navigationFullWidth);
  }

  return (
    <div className={s.navBackground}>
      <ul className={navClassName}>
        <li className={s.staticLink}>
          <Logo />
        </li>
        {showUniversalSearch ? (
          <li>
            <UniversalSearch location={location} />
          </li>
        ) : null}
        <li className={s.spacer} />
        {currentUser ? (
          <>
            <li key="search-link" className={s.navigationLink}>
              <NavLink
                to={routes.listingSearch()}
                data-testid="searchLink"
                onClick={trackers.searchClicked}
              >
                <span className={s.icon}>
                  <CustomIcon type="magnifying-glass" />
                </span>
                <span className={s.vtsNavText}>{t('search')}</span>
              </NavLink>
            </li>
            <li key="tourbook-link" className={s.navigationLink}>
              <NavLink to={routes.tourbooks} onClick={trackers.tourbooksClicked}>
                <span className={s.icon}>
                  <CustomIcon type="tourbook" />
                </span>
                <span className={s.vtsNavText}>{t('tourbook')}</span>
              </NavLink>
            </li>
            <Favorites trackers={trackers} />
            <UserSettings trackers={trackers} currentUser={currentUser} />
            <VTSHomeButton />
            <OverflowOptionsForTablet />
          </>
        ) : null}
        {!currentUser && isCurrentUserLoaded && !hideLogInAndSignUp ? (
          <SignedOutNavigation />
        ) : null}
      </ul>
    </div>
  );
};

export default StandardNavigation;
